import moment from 'moment';
import { ModelBase } from '../abstract/model-base.model';
import { Partner } from '../partner/partner.model';
import { Account } from './account.model';
import { Currency } from '../core/currency.model';
import { CurrencyPipe } from '@angular/common';
import { StatusEnum } from '../../enums/status.enum';
import { tagAccents } from 'src/assets/themes/biifor/components/tag';

export class Subsidy extends ModelBase {
    static override CLASS_NAME: string = 'Subsidy';

    // Propriétés privées
    private _label!: string;
    private _amount!: number;
    private _date!: moment.Moment;
    private _partner!: Partner;
    private _account!: Account;
    private _reference?: string;
    private _currency!: Currency;

    // Champs calculés (read-only)
    private _amountOrg!: number;
    private _amountUsed!: number;
    private _amountUsedOrg!: number;
    private _amountRemaining!: number;
    private _amountRemainingOrg!: number;
    private _exchangeRate!: number;

    // Méthodes statiques pour la gestion des statuts
    public static statusSeverity(status: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' | undefined {
        switch (true) {
            case (status === StatusEnum.Pending):
                return 'info';
            case (status === StatusEnum.PartiallyUsed):
            case (status === StatusEnum.FullyUsed):
                return 'success';
            default:
                return undefined;
        }
    }

    public static statusLabel(status: string): string {
        switch (true) {
            case (status === StatusEnum.Pending):
                return 'Non consommée';
            case (status === StatusEnum.PartiallyUsed):
                return 'Partiellement consommée';
            case (status === StatusEnum.FullyUsed):
                return 'Totalement consommée';
            default:
                return '';
        }
    }

    public static tagAccent(status: string): Object | undefined {
        switch (true) {
            case (status === StatusEnum.FullyUsed):
                return tagAccents;
            default:
                return undefined;
        }
    }

    // Getters/setters pour les statuts (instance)
    public get statusSeverity(): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' | undefined {
        switch (true) {
            case (this.hasStatus(StatusEnum.Pending)):
                return Subsidy.statusSeverity(StatusEnum.Pending);
            case (this.hasStatus(StatusEnum.PartiallyUsed)):
                return Subsidy.statusSeverity(StatusEnum.PartiallyUsed);
            case (this.hasStatus(StatusEnum.FullyUsed)):
                return Subsidy.statusSeverity(StatusEnum.FullyUsed);
            default:
                return Subsidy.statusSeverity('');
        }
    }

    public get statusLabel(): string {
        switch (true) {
            case (this.hasStatus(StatusEnum.Pending)):
                return Subsidy.statusLabel(StatusEnum.Pending);
            case (this.hasStatus(StatusEnum.PartiallyUsed)):
                return Subsidy.statusLabel(StatusEnum.PartiallyUsed);
            case (this.hasStatus(StatusEnum.FullyUsed)):
                return Subsidy.statusLabel(StatusEnum.FullyUsed);
            default:
                return Subsidy.statusLabel('');
        }
    }

    public get tagAccent(): Object | undefined {
        switch (true) {
            case (this.hasStatus(StatusEnum.FullyUsed)):
                return Subsidy.tagAccent(StatusEnum.FullyUsed);
            default:
                return Subsidy.tagAccent('');
        }
    }

    // Méthode toString pour les affichages
    public override toString(withAmount: boolean = false): string {
        let label = this.label;
        if (this.partner) {
            label += ' - ' + this.partner.name;
        }
        if (this.date) {
            label += ' - ' + this.date.format('DD/MM/YYYY');
        }
        if (this.reference && this.reference.length > 0) {
            label += ' - ' + this.reference;
        }
        if (withAmount && this.currency) {
            let pipe = new CurrencyPipe('fr');
            label += ' - ' + pipe.transform(this.amount, this.currency.code, 'code')!;
        }
        return label;
    }

    // Accesseurs (getters/setters)
    public get account(): Account {
        return this._account;
    }
    public set account(value: any) {
        this._setValue('_account', value, 'Account');
    }

    public get amount(): number {
        return this._amount;
    }
    public set amount(value: any) {
        this._setValue('_amount', value, 'float');
    }

    public get amountOrg(): number {
        return this._amountOrg;
    }
    public set amountOrg(value: any) {
        this._setValue('_amountOrg', value, 'float');
    }

    public get amountRemaining(): number {
        return this._amountRemaining;
    }
    public set amountRemaining(value: any) {
        this._setValue('_amountRemaining', value, 'float');
    }

    public get amountRemainingOrg(): number {
        return this._amountRemainingOrg;
    }
    public set amountRemainingOrg(value: any) {
        this._setValue('_amountRemainingOrg', value, 'float');
    }

    public get amountUsed(): number {
        return this._amountUsed;
    }
    public set amountUsed(value: any) {
        this._setValue('_amountUsed', value, 'float');
    }

    public get amountUsedOrg(): number {
        return this._amountUsedOrg;
    }
    public set amountUsedOrg(value: any) {
        this._setValue('_amountUsedOrg', value, 'float');
    }

    public get currency(): Currency {
        return this._currency;
    }
    public set currency(value: any) {
        this._setValue('_currency', value, 'Currency');
    }

    public get date(): moment.Moment {
        return this._date;
    }
    public set date(value: any) {
        this._setValue('_date', value, 'datetime');
    }

    public get exchangeRate(): number {
        return this._exchangeRate;
    }
    public set exchangeRate(value: any) {
        this._setValue('_exchangeRate', value, 'float', { precision: 5});
    }

    public get label(): string {
        return this._label;
    }
    public set label(value: any) {
        this._setValue('_label', value, 'string');
    }

    public get partner(): Partner {
        return this._partner;
    }
    public set partner(value: any) {
        this._setValue('_partner', value, 'Partner');
    }

    public get reference(): string | undefined {
        return this._reference;
    }
    public set reference(value: any) {
        this._setValue('_reference', value, 'string');
    }
}
